// ============================================================
// Plan Data Model – NextInvoice
// ============================================================

export interface PlanDefinition {
  id: string;
  name: string;
  /** One-liner that captures the plan's core promise */
  tagline: string;
  /** Number of module points included. null = unbegrenzt */
  pointsIncluded: number | null;
  /** Monthly price in EUR. null = individuell / Preis folgt */
  priceMonthly: number | null;
  /** Highlighted / "most popular" plan */
  highlight?: boolean;
  /** Short bullets describing the typical buyer */
  recommendedFor: string[];
  /** CTA button label */
  ctaLabel: string;
}

export const plans: PlanDefinition[] = [
  {
    id: "starter",
    name: "Starter",
    tagline: "Schnell starten, direkt produktiv.",
    pointsIncluded: 6,
    priceMonthly: 29,
    highlight: false,
    recommendedFor: [
      "Freelancer & Einzelunternehmer",
      "Einsteiger ohne komplexe Prozesse",
      "Bis zu 10 Nutzer",
    ],
    ctaLabel: "Kostenlos testen",
  },
  {
    id: "professional",
    name: "Professional",
    tagline: "Mehr Module, mehr Möglichkeiten.",
    pointsIncluded: 12,
    priceMonthly: 79,
    highlight: true,
    recommendedFor: [
      "Wachsende Teams & KMUs",
      "Kombination aus Vertrieb + HR + Lager",
      "Bis zu 75 Nutzer",
    ],
    ctaLabel: "Kostenlos testen",
  },
  {
    id: "business",
    name: "Business",
    tagline: "Volle Modulvielfalt für etablierte Unternehmen.",
    pointsIncluded: 20,
    priceMonthly: 149,
    highlight: false,
    recommendedFor: [
      "Etablierte GmbHs & AGs",
      "Komplexe Prozesslandschaften",
      "Bis zu 250 Nutzer",
    ],
    ctaLabel: "Kostenlos testen",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    tagline: "Individuelle Lösungen ohne Grenzen.",
    pointsIncluded: null, // unbegrenzt
    priceMonthly: null, // individuell
    highlight: false,
    recommendedFor: [
      "Konzerne & Großunternehmen",
      "On-Premise-Option verfügbar",
      "Unbegrenzte Nutzeranzahl",
    ],
    ctaLabel: "Kontakt aufnehmen",
  },
];

// ── Derived helpers ────────────────────────────────────────────

/** Format price for display */
export function formatPrice(priceMonthly: number | null): string {
  if (priceMonthly === null) return "Individuell";
  return `${priceMonthly} €`;
}

/** Format points for display */
export function formatPoints(pointsIncluded: number | null): string {
  if (pointsIncluded === null) return "Unbegrenzt";
  return `${pointsIncluded} Punkte`;
}

/** Check if a given set of used points fits within a plan */
export function planFitsPoints(
  plan: PlanDefinition,
  usedPoints: number
): boolean {
  if (plan.pointsIncluded === null) return true;
  return usedPoints <= plan.pointsIncluded;
}

/** Find the smallest plan that fits a given point budget */
export function recommendPlan(
  usedPoints: number
): PlanDefinition | undefined {
  return plans.find((p) => planFitsPoints(p, usedPoints));
}
